/**
 * The vendor's tenants (phase 7b, D13): name, status, and the counts — users,
 * sites, nodes, live productions, cloud-production hours this month — with
 * create, suspend and reactivate for a `vendor.admin` (`canAdmin`). The
 * vendor's own tenant cannot be suspended, so it offers neither.
 */
import type { VendorTenantWire } from '@norna/api-contract/vendor.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { isPlainClick } from '../../lib/links.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { UNKNOWN_HINT } from '../../lib/openEnum.js';
import { tenantKindLabel, tenantKindTone } from '../../lib/vendor.js';

export type TenantsTableProps = {
  readonly tenants: readonly VendorTenantWire[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly canAdmin: boolean;
  readonly onCreate: () => void;
  readonly onSuspend: (tenantId: string) => void;
  readonly onReactivate: (tenantId: string) => void;
  readonly onOpen: (tenantId: string) => void;
  readonly onRetry: () => void;
};

export function vendorTenantHref(tenantId: string): string {
  return `/vendor/tenants/${encodeURIComponent(tenantId)}`;
}

/** Hours to one decimal: what the month's metering shows. */
export function hoursText(hours: number): string {
  return `${hours.toFixed(1)} h`;
}

export function TenantStatusBadge({ tenant }: { readonly tenant: Pick<VendorTenantWire, 'status' | 'isVendor'> }) {
  if (tenant.isVendor) return <Badge variant="standby">staff</Badge>;
  return (
    <Badge
      variant={tenant.status === 'active' ? 'success' : tenant.status === 'suspended' ? 'destructive' : 'secondary'}
      title={tenant.status === 'unknown' ? UNKNOWN_HINT : undefined}
      data-testid="tenant-status"
    >
      {tenant.status}
    </Badge>
  );
}

function TenantRow({ tenant: t, canAdmin, onSuspend, onReactivate, onOpen }: { readonly tenant: VendorTenantWire } & Pick<TenantsTableProps, 'canAdmin' | 'onSuspend' | 'onReactivate' | 'onOpen'>) {
  return (
    <TableRow data-testid="tenant-row" data-tenant={t.tenantId}>
      <TableCell>
        <a
          href={vendorTenantHref(t.tenantId)}
          className="font-medium hover:underline"
          data-testid="open-tenant"
          onClick={(event) => {
            if (!isPlainClick(event)) return;
            event.preventDefault();
            onOpen(t.tenantId);
          }}
        >
          {t.name}
        </a>
        {t.kind === 'demo' ? (
          <Badge variant={tenantKindTone(t.kind)} className="ml-2" data-testid="tenant-kind">
            {tenantKindLabel(t.kind)}
          </Badge>
        ) : null}
        <div className="font-mono text-xs text-muted-foreground">{t.tenantId}</div>
      </TableCell>
      <TableCell>
        <TenantStatusBadge tenant={t} />
        {t.status === 'suspended' && t.suspendedReason !== null ? <div className="text-xs text-muted-foreground">{t.suspendedReason}</div> : null}
      </TableCell>
      <TableCell className="text-right font-mono text-sm">{t.counts.users}</TableCell>
      <TableCell className="text-right font-mono text-sm">{t.counts.sites}</TableCell>
      <TableCell className="text-right font-mono text-sm">{t.counts.nodes}</TableCell>
      <TableCell className="text-right font-mono text-sm">{t.counts.liveProductions}</TableCell>
      <TableCell className="text-right font-mono text-sm">{hoursText(t.counts.cloudProductionHoursThisMonth)}</TableCell>
      {canAdmin ? (
        <TableCell className="text-right">
          {/* A standing from a newer cloud (`unknown`, D37) is offered neither action: which one applies is not known. */}
          {t.isVendor || t.status === 'unknown' ? null : t.status === 'active' ? (
            <Button size="sm" variant="outline" onClick={() => onSuspend(t.tenantId)} data-testid="suspend-tenant">
              Suspend
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onReactivate(t.tenantId)} data-testid="reactivate-tenant">
              Reactivate
            </Button>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export function TenantsTable({ tenants, loading, error, canAdmin, onCreate, onSuspend, onReactivate, onOpen, onRetry }: TenantsTableProps) {
  return (
    <Card data-testid="tenants-table">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Tenants</CardTitle>
          <CardDescription>Every tenant of this region, its footprint and this month’s cloud-production hours.</CardDescription>
        </div>
        {canAdmin ? (
          <Button onClick={onCreate} data-testid="create-tenant">
            <PlusIcon />
            New tenant
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load tenants: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && tenants.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading tenants…</p> : null}
        {!loading && error === null && tenants.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No tenants yet.</p> : null}
        {tenants.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Users</TableHead>
                <TableHead className="text-right">Sites</TableHead>
                <TableHead className="text-right">Nodes</TableHead>
                <TableHead className="text-right">Live</TableHead>
                <TableHead className="text-right">Cloud hours (month)</TableHead>
                {canAdmin ? <TableHead className="w-32" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TenantRow key={t.tenantId} tenant={t} canAdmin={canAdmin} onSuspend={onSuspend} onReactivate={onReactivate} onOpen={onOpen} />
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
